import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WBSButton } from '@/common/WBSButton';
import type { Student, StudentBoardAction } from '~/types/studentTypes';

type ActionsCellProps = {
  actions: StudentBoardAction[];
  row: Student;
  onAdopt?: (studentId: string) => void | Promise<void>;
  onPassOn?: (studentId: string) => void | Promise<void>;
  onEncourage?: (studentId: string) => void | Promise<void>;
  onAssign?: (studentId: string) => void | Promise<void>;
};

/**
 * One actions cell for both student boards.
 *
 * Renders the server's per-row `actions` as given and ignores an action it has
 * no button for, so the server can add an action without breaking this cell.
 */
export function ActionsCell({
  actions,
  row,
  onAdopt,
  onPassOn,
  onEncourage,
  onAssign,
}: ActionsCellProps) {
  const { t } = useTranslation();

  return (
    <ActionsCellContainer>
      {actions.map(({ action, disabled, count }) => {
        switch (action) {
          case 'encourage': {
            // The received count goes in the label. Disabled means this helper
            // already encouraged the student or the student has reached the
            // encouragement cap, and it renders as a non-clickable pill, so the
            // handler is only wired when enabled.
            const label = disabled ? t('wbs.encouraged') : t('wbs.encourage');
            return (
              <WBSButton
                key={action}
                alternate
                disabled={disabled}
                onClick={
                  disabled ? undefined : () => void onEncourage?.(row.id)
                }
              >
                {count > 0 ? `${label} (${count})` : label}
              </WBSButton>
            );
          }
          case 'adopt':
            // Disabled renders the non-clickable Not Adoptable pill, e.g. when
            // the helper's language or gender preference rules the student out.
            return disabled ? (
              <WBSButton key={action} disabled>
                {t('wbs.not-adoptable')}
              </WBSButton>
            ) : (
              <WBSButton key={action} onClick={() => onAdopt?.(row.id)}>
                {t('wbs.adopt')}
              </WBSButton>
            );
          case 'pass_on':
            return (
              <WBSButton
                key={action}
                alternate
                disabled={disabled}
                onClick={() => onPassOn?.(row.id)}
              >
                {t('wbs.pass-on')}
              </WBSButton>
            );
          case 'assign_to':
            // The server sends this only when the caller admins a congregation
            // or custom study group that claims the student, and never
            // disabled.
            return (
              <WBSButton
                key={action}
                alternate
                disabled={disabled}
                onClick={() => onAssign?.(row.id)}
              >
                {t('wbs.assign-to')}
              </WBSButton>
            );
          default:
            return null;
        }
      })}
    </ActionsCellContainer>
  );
}

const ActionsCellContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;
